import { create } from 'zustand';
import { getUserProfile, updateUserProfile } from '../services/api';
import { extractAcademicProfileFromApiResponse } from '../utils/academicProfileFromApi';
import { userApiToCodingProfiles } from '../utils/codingProfiles';
import sessionAuthStore from './authStore';

function normalizeCodingProfilesPayload(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((p) => ({
      type: String(p?.type ?? '').trim(),
      username: String(p?.username ?? '').trim(),
    }))
    .filter((p) => p.type && p.username);
}

function githubUsernameFromApiUser(userData) {
  const cp = userData.codingProfiles;
  if (Array.isArray(cp)) {
    const g = cp.find((p) => p.type === 'github');
    if (g?.username) return String(g.username).trim();
  }
  return userData.githubUsername || '';
}

/** Deep-enough clone for profile edits (branch, year, codingProfiles / github). */
function cloneProfile(p) {
  if (!p) return null;
  const next = { ...p };
  if (Array.isArray(p.codingProfiles)) {
    next.codingProfiles = p.codingProfiles.map((x) => ({ ...x }));
  }
  return next;
}

const useAuthStore = create((set, get) => ({
  // ── Auth State ──
  user: null,
  token: null,
  loading: true,
  isAuthenticated: false,

  // ── Profile State ──
  profile: null,
  /** Last saved / loaded profile; used to discard unsaved edits */
  profileBaseline: null,
  profileLoading: false,
  profileError: '',
  profileSuccess: '',
  profileSaving: false,

  // ── Snackbar State ──
  snackbar: { open: false, message: '', severity: 'info' },

  // ── Computed Getters ──
  get isStudent() {
    return get().user?.role === 'student';
  },
  get isFaculty() {
    return get().user?.role === 'faculty';
  },

  // ── Auth Actions ──
  initialize: () => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUser && token) {
      const parsed = JSON.parse(storedUser);
      set({
        user: { ...parsed, token },
        token,
        isAuthenticated: true,
        loading: false,
      });
    } else {
      set({ loading: false });
    }
  },

  login: (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));

    set({
      user: { ...userData, token },
      token,
      isAuthenticated: true,
      snackbar: {
        open: true,
        message: `Welcome back, ${userData.name}!`,
        severity: 'success',
      },
    });
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      profile: null,
      profileBaseline: null,
      snackbar: {
        open: true,
        message: 'You have successfully logged out.',
        severity: 'info',
      },
    });
  },

  closeSnackbar: () => {
    set((state) => ({
      snackbar: { ...state.snackbar, open: false },
    }));
  },

  // ── Profile Actions ──
  fetchProfile: async () => {
    set({ profileLoading: true, profileError: '' });
    try {
      const response = await getUserProfile();
      const userData = response.data.user;
      sessionAuthStore.setState({
        academicProfile: extractAcademicProfileFromApiResponse(userData, 'GET /users/me'),
      });
      const base = {
        name: userData.name || '',
        email: userData.email || '',
        role: userData.role || '',
        branch: userData.branch || '',
        yearOfStudy: userData.yearOfStudy || '',
        profilePic: userData.profilePic || '',
      };
      const profile =
        userData.role === 'student'
          ? {
              ...base,
              codingProfiles:
                Array.isArray(userData.codingProfiles) && userData.codingProfiles.length > 0
                  ? normalizeCodingProfilesPayload(userData.codingProfiles)
                  : userApiToCodingProfiles(userData),
            }
          : {
              ...base,
              githubUsername: githubUsernameFromApiUser(userData),
            };
      set({ profile, profileBaseline: cloneProfile(profile), profileLoading: false });
      return profile;
    } catch (err) {
      console.error('Error fetching profile:', err);
      set({ profileLoading: false, profileError: 'Failed to load profile data.' });
      throw err;
    }
  },

  updateProfileField: (field, value) => {
    set((state) => ({
      profile: { ...state.profile, [field]: value },
    }));
  },

  /** Restore profile fields to last loaded / saved state (discard unsaved edits). */
  resetProfile: () => {
    const { profileBaseline } = get();
    if (!profileBaseline) return;
    set({
      profile: cloneProfile(profileBaseline),
      profileError: '',
      profileSuccess: '',
    });
  },

  /** @param {{ type: string, username: string }} entry */
  addCodingProfile: (entry) => {
    set((state) => {
      const profile = state.profile;
      if (!profile || profile.role !== 'student') return state;
      const list = profile.codingProfiles ?? [];
      const username = (entry.username ?? '').trim();
      if (!entry.type || !username) return state;
      if (list.some((p) => p.type === entry.type)) return state;
      return {
        profile: {
          ...profile,
          codingProfiles: [...list, { type: entry.type, username }],
        },
      };
    });
  },

  /** @param {number} index @param {{ username?: string, type?: string }} partial */
  updateCodingProfile: (index, partial) => {
    set((state) => {
      const profile = state.profile;
      if (!profile || profile.role !== 'student') return state;
      const list = profile.codingProfiles ?? [];
      if (index < 0 || index >= list.length) return state;
      const next = [...list];
      const cur = next[index];
      next[index] = {
        ...cur,
        ...(partial.type !== undefined ? { type: partial.type } : {}),
        ...(partial.username !== undefined
          ? { username: String(partial.username).trim() }
          : {}),
      };
      return { profile: { ...profile, codingProfiles: next } };
    });
  },

  /** @param {number} index */
  removeCodingProfile: (index) => {
    set((state) => {
      const profile = state.profile;
      if (!profile || profile.role !== 'student') return state;
      const list = profile.codingProfiles ?? [];
      if (index < 0 || index >= list.length) return state;
      return {
        profile: {
          ...profile,
          codingProfiles: list.filter((_, i) => i !== index),
        },
      };
    });
  },

  /** Remove by platform id (`type`), e.g. `"leetcode"`. Immutable. */
  removeCodingProfileByType: (type) => {
    set((state) => {
      const profile = state.profile;
      if (!profile || profile.role !== 'student' || !type) return state;
      const list = profile.codingProfiles ?? [];
      return {
        profile: {
          ...profile,
          codingProfiles: list.filter((p) => p.type !== type),
        },
      };
    });
  },

  /** Update by platform id (`type`). Immutable. No-op if type missing. */
  updateCodingProfileByType: (type, partial) => {
    set((state) => {
      const profile = state.profile;
      if (!profile || profile.role !== 'student' || !type) return state;
      const list = profile.codingProfiles ?? [];
      const idx = list.findIndex((p) => p.type === type);
      if (idx === -1) return state;
      const next = [...list];
      const cur = next[idx];
      next[idx] = {
        ...cur,
        ...(partial.type !== undefined ? { type: partial.type } : {}),
        ...(partial.username !== undefined
          ? { username: String(partial.username).trim() }
          : {}),
      };
      return { profile: { ...profile, codingProfiles: next } };
    });
  },

  saveProfile: async () => {
    const { profile } = get();
    set({ profileSaving: true, profileError: '', profileSuccess: '' });
    try {
      const payload = {
        branch: profile.branch,
        yearOfStudy: profile.yearOfStudy,
      };
      if (profile.role === 'student') {
        payload.codingProfiles = normalizeCodingProfilesPayload(profile.codingProfiles);
      } else {
        const gh = (profile.githubUsername ?? '').trim();
        payload.codingProfiles = gh ? [{ type: 'github', username: gh }] : [];
      }
      const { data } = await updateUserProfile(payload);
      if (data?.user) {
        sessionAuthStore.setState({
          academicProfile: extractAcademicProfileFromApiResponse(data.user, 'PUT /users/me'),
        });
      }
      const saved = get().profile;
      set({
        profileSaving: false,
        profileSuccess: 'Profile updated successfully!',
        profileBaseline: cloneProfile(saved),
      });
    } catch (err) {
      console.error('Error updating profile:', err);
      set({
        profileSaving: false,
        profileError: 'Failed to update profile. Please try again.',
      });
    }
  },
}));

export default useAuthStore;
